import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, EyeOff, Loader2, Star } from "lucide-react";
import MiniHeader from "@/components/site/MiniHeader";
import AdminNav from "@/components/site/AdminNav";
import { ownerApi } from "@/lib/ownerApi";

const STATUS = { pending: ["Waiting for you", "text-[#E6B89C]"], approved: ["On your homepage", "text-[#D4AF37]"], hidden: ["Hidden", "text-[#A0988E]"] };

function Note({ n, onStatus }) {
  const [label, color] = STATUS[n.status];
  return (
    <article data-testid={`admin-note-${n.id}`} className="card-surface p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="text-xs text-[#A0988E]">
          For <span className="text-[#F5F0E8]">{n.loved_one_name}</span> · from {n.family_name}
          {n.owner_path && <a href={n.owner_path} className="ml-2 underline decoration-[#C5A55A]/40 hover:text-[#F5F0E8]">family page</a>}
        </div>
        <span data-testid={`admin-note-status-${n.id}`} className={`text-xs ${color}`}>{label}</span>
      </div>
      {n.rating && <div className="mt-3 flex gap-0.5">{[1, 2, 3, 4, 5].map((s) => <Star key={s} size={14} className={s <= n.rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-[#A0988E]/40"} />)}</div>}
      <blockquote className="serif italic text-xl text-[#F5F0E8]/95 leading-snug mt-3">&ldquo;{n.message}&rdquo;</blockquote>
      <div className="mt-3 text-sm text-[#F5F0E8]">{n.display_name}{n.place && <span className="text-[#A0988E]"> · {n.place}</span>}</div>
      <div className="mt-5 flex gap-3">
        {n.status !== "approved" && (
          <button data-testid={`admin-note-approve-${n.id}`} onClick={() => onStatus(n, "approved")} className="btn-primary text-xs py-2 px-4"><Check size={13} /> Show on homepage</button>
        )}
        {n.status !== "hidden" && (
          <button data-testid={`admin-note-hide-${n.id}`} onClick={() => onStatus(n, "hidden")} className="btn-ghost text-xs py-2 px-4"><EyeOff size={13} /> Hide</button>
        )}
      </div>
    </article>
  );
}

export default function AdminNotesPage() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState(null);

  useEffect(() => {
    ownerApi.get("/admin/reviews").then(({ data }) => setNotes(data.reviews)).catch(() => navigate("/owner", { replace: true }));
  }, [navigate]);

  const onStatus = async (n, status) => {
    try {
      await ownerApi.post(`/admin/reviews/${n.id}`, { status });
      setNotes((list) => list.map((x) => (x.id === n.id ? { ...x, status } : x)));
      toast.success(status === "approved" ? "Now showing on your homepage." : "Hidden from your homepage.");
    } catch {
      toast.error("Could not update the note.");
    }
  };

  if (!notes) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#C5A55A]" /></div>;
  const waiting = notes.filter((n) => n.status === "pending").length;
  return (
    <div data-testid="admin-notes-page" className="min-h-screen">
      <MiniHeader right={<AdminNav />} />
      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-12">
        <div className="overline mb-3">Owner</div>
        <h1 className="serif text-4xl sm:text-5xl font-light text-[#F5F0E8]">Thank-you notes</h1>
        <p className="mt-3 text-sm text-[#A0988E] spectral">{waiting ? `${waiting} waiting for your approval.` : "Nothing waiting."} Only approved notes appear on your homepage.</p>
        <div className="mt-8 space-y-4">
          {notes.length ? notes.map((n) => <Note key={n.id} n={n} onStatus={onStatus} />) : (
            <p data-testid="admin-no-notes" className="card-surface p-10 text-center text-[#A0988E] spectral italic">No notes yet. Families can leave one after watching their film.</p>
          )}
        </div>
      </main>
    </div>
  );
}
